import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";

const EmailAliasGeneratorContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline */}
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ fontSize: "1.75rem" }}
        gutterBottom
      >
        Generate Email Aliases Instantly{" "}
        <AlternateEmailIcon color="primary" sx={{ verticalAlign: "middle" }} />
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Easily Organize & Filter Your Incoming Emails
      </Typography>

      {/* Problem & Solution */}
      <Typography variant="body1" paragraph>
        Using the same email for everything can clutter your inbox. Manually
        sorting emails from newsletters, apps, and services is a hassle.
      </Typography>
      <Typography
        variant="body1"
        paragraph
        sx={{ fontWeight: "bold", color: "primary.main" }}
      >
        Our Email Alias Generator lets you create custom aliases to easily
        group, tag, and filter emails by purpose or source.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Use Email Aliases?
      </Typography>
      <ul
        style={{
          textAlign: "left",
          display: "inline-block",
          listStyleType: "none",
          paddingLeft: 0,
        }}
      >
        <li>
          ✅ <strong>Instant Setup:</strong> Create aliases on the fly
        </li>
        <li>
          🧠 <strong>Smart Organization:</strong> Group emails by project,
          platform, or client
        </li>
        <li>
          📨 <strong>Inbox Filters:</strong> Use aliases to apply email filters
          automatically
        </li>
        <li>
          🗑️ <strong>Easy Cleanup:</strong> Disable unused aliases anytime
        </li>
        <li>
          🔧 <strong>Flexible Usage:</strong> Use for trials, signups, or
          testing
        </li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How It Works
      </Typography>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          p: 3,
          borderRadius: 2,
          textAlign: "left",
          display: "inline-block",
        }}
      >
        <ol>
          <li>
            <strong>Enter</strong> a base name or let us generate one
          </li>
          <li>
            <strong>Click</strong> “Generate Alias”
          </li>
          <li>
            <strong>Set</strong> your forwarding destination
          </li>
          <li>
            <strong>Done!</strong> Use the alias to receive grouped emails
          </li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Loved by Freelancers, Developers & Productivity Geeks
      </Typography>
      <Box sx={{ fontStyle: "italic", mb: 3 }}>
        <Typography paragraph>
          "I use aliases to track where emails are coming from and route them
          into folders. Makes life so much easier."
        </Typography>
        <Typography>- Nikhil T., Indie Hacker</Typography>
      </Box>

      {/* FAQ Section */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: "inline-block" }}>
        <Typography variant="body1" fontWeight="bold">
          📥 Do aliases receive emails normally?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Yes, all aliases forward emails to your chosen inbox automatically.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🧹 Can I turn off an alias later?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Yes, aliases can be disabled or deleted anytime.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🎯 What’s the benefit of using multiple aliases?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          You can easily organize incoming emails by service, client, or topic
          using filters or tags.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          💰 Is the tool free to use?
        </Typography>
        <Typography variant="body2" paragraph>
          Free users spend 1 credit per alias. Paid users generate unlimited
          aliases with 0 credit cost 🎁.
        </Typography>
      </Box>

      {/* CTA */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: "bold" }}>
          Ready to generate your first alias?
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={scrollToTop}
          sx={{
            fontSize: "1.1rem",
            py: 1.5,
            px: 4,
            fontWeight: "bold",
          }}
        >
          Generate Alias →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 credit per alias for Free users. Unlimited for Paid users.
        </Typography>
      </Box>
    </Container>
  );
};

export default EmailAliasGeneratorContent;
